import React, { useMemo, useState, useContext } from "react"
import ConditionalRender from "../../../ConditionalRender.js"
import { Icons } from "../Icons/index.js"
import { ClickableDropdown, DropdownContext } from "../Dropdown/Dropdown.jsx"
import { DropdownMenu } from "../Dropdown/DropdownMenu.jsx"
import { DropdownItem } from "../Dropdown/DropdownItem.jsx"

/**
 * Mirrors InputBox size classes so the trigger looks identical to an InputBox.
 */
const SearchableSelectBoxSizes = {
    Small: "input-box-small",
    Medium: "input-box-medium",
    Large: "input-box-large",
}

const SearchableSelectBoxSizeFonts = {
    Small: "body-s-semibold",
    Medium: "body-s-semibold",
    Large: "body-m-semibold",
}

const SearchableSelectBoxTrigger = ({ triggerClasses, selectedLabel, placeholder, triggerStyle, placeholderClass, placeholderStyle }) => {
    const { open } = useContext(DropdownContext)
    return (
        <div className={triggerClasses} style={triggerStyle}>
            <span
                className={selectedLabel ? "searchable-select-box__label" : placeholderClass}
                style={selectedLabel ? undefined : placeholderStyle}
            >
                {selectedLabel || placeholder}
            </span>
            <Icons name={open ? "chevron-up" : "chevron-down"} size={16} className="ml-s" />
        </div>
    )
}

/**
 * SearchableSelectBox — Single-select dropdown with inline search filtering.
 *
 * Composes ClickableDropdown + DropdownMenu (same as SelectBox) but adds a
 * search input at the top of the menu so users can quickly filter long lists.
 *
 * Options accept plain strings, {label, value} objects, or a plain object { displayLabel: value }.
 * handleOnChange receives the selected value directly (not a native event).
 *
 * @param {string}   Size           - 'Small' | 'Medium' | 'Large'
 * @param {string}   label          - Label text shown above the trigger
 * @param {boolean}  showLabel      - Whether to render the label
 * @param {string}   placeholder    - Shown in the trigger when no value is selected
 * @param {string}   searchPlaceholder - Placeholder inside the search input
 * @param {Array|Object} options    - string[], {label, value}[], or { displayLabel: value }
 * @param {string}   value          - Controlled selected value
 * @param {func}     handleOnChange - Called with the selected value (string)
 * @param {string}   error          - Triggers error styling on the trigger
 * @param {boolean}  disabled       - Disabled state
 * @param {string}   variant        - 'default' (border + bg) | 'ghost' (no border, no bg, hover only)
 */
export const SearchableSelectBox = ({
    Size = "Medium",
    label = "",
    showLabel = false,
    labelClass = "input-box-label body-s-semibold",
    value,
    placeholder = "Select...",
    searchPlaceholder = "Search...",
    placeholderClass = "select-box__placeholder",
    placeholderStyle = {},
    options = [],
    customClass = "",
    customStyle = {},
    handleOnChange,
    error = "",
    disabled = false,
    fontClass = null,
    variant = "default",
    containerClass = "",
    triggerStyle = {},
    menuCustomStyle = {},
    align = "left",
}) => {
    const [query, setQuery] = useState("")

    const triggerClasses = useMemo(() =>
        [
            "input-box",
            SearchableSelectBoxSizes[Size],
            "select-box__trigger",
            fontClass ?? SearchableSelectBoxSizeFonts[Size],
            variant === "ghost" && "select-box__trigger--ghost",
            error && "input-box-error",
            disabled && "input-box-disabled",
            customClass,
        ].filter(Boolean).join(" "),
        [Size, fontClass, variant, error, disabled, customClass]
    )

    const selectedLabel = useMemo(() =>
        options.find(opt => opt.value === value)?.label,
        [value, options]
    )

    const filtered = useMemo(() => {
        if (!query) return options
        const q = query.toLowerCase()
        return options.filter(opt => opt.label.toLowerCase().includes(q))
    }, [query, options])

    const handleOpen = () => {
        setQuery("")
    }

    return (
        <div className={containerClass} style={{ ...customStyle }}>
            <ConditionalRender if={showLabel && label !== ""}>
                <div className={labelClass}>{label}</div>
            </ConditionalRender>
            <ClickableDropdown disabled={disabled} onOpen={handleOpen} rootStyle={{ width: '100%' }}>
                <SearchableSelectBoxTrigger
                    triggerClasses={triggerClasses}
                    triggerStyle={triggerStyle}
                    selectedLabel={selectedLabel}
                    placeholder={placeholder}
                    placeholderClass={placeholderClass}
                    placeholderStyle={placeholderStyle}
                />
                <DropdownMenu usePortal align={align} customClasses="searchable-select-box__menu" customStyle={menuCustomStyle} innerStyle={{ padding: 0 }}>
                    <SearchableSelectBoxMenuContent
                        searchPlaceholder={searchPlaceholder}
                        query={query}
                        setQuery={setQuery}
                        filtered={filtered}
                        value={value}
                        handleOnChange={handleOnChange}
                    />
                </DropdownMenu>
            </ClickableDropdown>
        </div>
    )
}

/** Inner menu content — search input + filtered option list */
const SearchableSelectBoxMenuContent = ({ searchPlaceholder, query, setQuery, filtered, value, handleOnChange }) => {
    return (
        <>
            <div className="searchable-select-box__search">
                <Icons name="search" size={16} className="searchable-select-box__search-icon" />
                <input
                    autoFocus
                    className="input-box searchable-select-box__search-input"
                    type="text"
                    placeholder={searchPlaceholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                />
            </div>
            <div className="searchable-select-box__options">
                {filtered.length === 0 && (
                    <div className="searchable-select-box__no-match body-s color-text-tertiary">
                        No matches
                    </div>
                )}
                {filtered.map(opt => (
                    <DropdownItem
                        key={opt.value}
                        onClick={() => handleOnChange?.(opt.value)}
                        selected={opt.value === value}
                    >
                        {opt.label}
                    </DropdownItem>
                ))}
            </div>
        </>
    )
}
